// Every single-field edit on a staff member, in one place — the Roster's ⋯ menu
// and the staff detail modal (BL-39) open the SAME dialogs, so one field is
// changed at a time and the two surfaces cannot drift apart.
//
// Moved out of RosterRowActions. Each dialog now runs its own mutation and
// reports the result as a toast, instead of a page-level error line that only
// three of the five mutations ever reached.

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@bednbody/ui/components/alert-dialog";
import { HubSelect } from "@/components/HubSelect";
import { Button } from "@bednbody/ui/components/button";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue,
} from "@bednbody/ui/components/combobox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Field, FieldDescription, FieldLabel } from "@bednbody/ui/components/field";
import { Input } from "@bednbody/ui/components/input";
import { RadioGroup, RadioGroupItem } from "@bednbody/ui/components/radio-group";
import { Textarea } from "@bednbody/ui/components/textarea";
import { useState } from "react";
import { toast } from "sonner";
import { useHubsList } from "../../ops-dashboard/ops-dashboard.hooks";
import type { RiderVehicle } from "../../staff-media/staff-media.hooks";
import {
  ASSIGNABLE_STAFF_ROLES,
  type AssignableStaffRole,
  useLatestTierScore,
  usePromoteStaffRole,
  useReassignStaffHub,
  useReleaseRankToSystem,
  useRenameStaff,
  useSetRiderRank,
  useSetTherapistRank,
  useToggleStaffActive,
} from "../roster.hooks";
import { ResetPasswordDialog } from "./ResetPasswordDialog";
import { SetPlateDialog } from "./SetPlateDialog";
import type { RosterPerson } from "./types";

export type StaffEditKind =
  | "name"
  | "toggle"
  | "role"
  | "hub"
  | "rider-rank"
  | "therapist-rank"
  | "plate"
  | "reset-password";

/** RIDER-031's four values, low to high. A fixed set that does not grow. */
export const RIDER_RANKS = ["bronze", "silver", "gold", "diamond"] as const;
export type RiderRank = (typeof RIDER_RANKS)[number];

export const rankLabel = (rank: string | null | undefined) =>
  rank ? rank.charAt(0).toUpperCase() + rank.slice(1) : "Bronze";

export const roleLabel = (role: string) => role.replace(/_/g, " ");

function reportError(title: string) {
  return (error: unknown) =>
    toast.error(title, { description: error instanceof Error ? error.message : undefined });
}

interface StaffEditDialogsProps {
  person: RosterPerson;
  /** Which edit is open; `null` = none. */
  dialog: StaffEditKind | null;
  onDialogChange: (dialog: StaffEditKind | null) => void;
  /** The rider's plate on record, if any — riders only (0279). */
  vehicle?: RiderVehicle | null;
}

export function StaffEditDialogs({
  person,
  dialog,
  onDialogChange,
  vehicle = null,
}: StaffEditDialogsProps) {
  const { data: hubs } = useHubsList();
  const rename = useRenameStaff();
  const toggleActive = useToggleStaffActive();
  const promoteRole = usePromoteStaffRole();
  const reassignHub = useReassignStaffHub();
  const setRiderRank = useSetRiderRank();
  const setTherapistRank = useSetTherapistRank();
  const releaseRank = useReleaseRankToSystem();
  // Only fetched while a rank dialog is open — the latest monthly score is
  // context for the decision, not roster data.
  const latestScore = useLatestTierScore(
    dialog === "rider-rank" || dialog === "therapist-rank" ? person.id : null,
  );

  const [pendingName, setPendingName] = useState(person.name);
  const [pendingRole, setPendingRole] = useState<AssignableStaffRole | null>(null);
  const [pendingHubId, setPendingHubId] = useState("");
  // RIDER-032 requires the reason on the audit record, so it is state here
  // rather than a field the dialog can submit without.
  const [pendingRank, setPendingRank] = useState<RiderRank | null>(null);
  const [rankReason, setRankReason] = useState("");
  // Remember which rank dialog was open, so the title holds while it fades out.
  const [rankKind, setRankKind] = useState<"rider-rank" | "therapist-rank">("rider-rank");

  // Each dialog starts from what is on record every time it opens.
  const [lastOpened, setLastOpened] = useState<StaffEditKind | null>(null);
  if (dialog !== lastOpened) {
    setLastOpened(dialog);
    if (dialog !== null) {
      setPendingName(person.name);
      setPendingRole(null);
      setPendingHubId("");
      setPendingRank(null);
      setRankReason("");
      if (dialog === "rider-rank" || dialog === "therapist-rank") setRankKind(dialog);
    }
  }

  const close = () => onDialogChange(null);
  const closeOnDismiss = (open: boolean) => {
    if (!open) close();
  };
  const trimmedName = pendingName.trim();
  const rankPending = setRiderRank.isPending || setTherapistRank.isPending || releaseRank.isPending;

  return (
    <>
      <ResetPasswordDialog
        onOpenChange={(open) => onDialogChange(open ? "reset-password" : null)}
        open={dialog === "reset-password"}
        inactive={!person.active}
        staffId={person.id}
        staffName={person.name}
      />
      {person.role === "rider" ? (
        <SetPlateDialog
          current={vehicle}
          onOpenChange={(open) => onDialogChange(open ? "plate" : null)}
          open={dialog === "plate"}
          riderId={person.id}
          riderName={person.name}
        />
      ) : null}

      {/* Name */}
      <Dialog onOpenChange={closeOnDismiss} open={dialog === "name"}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit name</DialogTitle>
            <DialogDescription>The name on {person.name}&rsquo;s staff account.</DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={`staff-name-${person.id}`}>Full name</FieldLabel>
            <Input
              autoComplete="off"
              id={`staff-name-${person.id}`}
              maxLength={120}
              onChange={(event) => setPendingName(event.target.value)}
              required
              type="text"
              value={pendingName}
            />
            <FieldDescription>First name, then surname.</FieldDescription>
          </Field>
          <DialogFooter>
            <Button disabled={rename.isPending} onClick={close} variant="outline">
              Cancel
            </Button>
            <Button
              disabled={!trimmedName || trimmedName === person.name}
              loading={rename.isPending}
              onClick={() =>
                rename.mutate(
                  { staffId: person.id, fullName: trimmedName },
                  {
                    onSuccess: () => {
                      toast.success("Name saved");
                      close();
                    },
                    onError: reportError("Couldn't save the name"),
                  },
                )
              }
            >
              Save name
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Active / Inactive */}
      <AlertDialog onOpenChange={closeOnDismiss} open={dialog === "toggle"}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {person.active ? "Deactivate" : "Activate"} staff member
            </AlertDialogTitle>
            <AlertDialogDescription>
              {person.active
                ? `${person.name} will no longer appear in dispatch queues and cannot accept new bookings.`
                : `${person.name} will become available for dispatch and can accept bookings.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={toggleActive.isPending}
              onClick={() => {
                toggleActive.mutate(
                  { staffId: person.id, active: !person.active },
                  { onError: reportError("Couldn't change their status") },
                );
                close();
              }}
              variant={person.active ? "destructive" : "default"}
            >
              {person.active ? "Deactivate" : "Activate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Role */}
      <Dialog onOpenChange={closeOnDismiss} open={dialog === "role"}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Role</DialogTitle>
            <DialogDescription>
              Select a new role for {person.name}. The change applies at their next sign-in or token
              refresh.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <p className="mb-2 block text-sm font-medium">Role</p>
            <Combobox
              items={[...ASSIGNABLE_STAFF_ROLES]}
              itemToStringLabel={roleLabel}
              onValueChange={(role) => setPendingRole(role as AssignableStaffRole)}
              value={pendingRole}
            >
              <ComboboxTrigger
                render={
                  <Button
                    className="w-full justify-between font-normal capitalize"
                    variant="outline"
                  />
                }
              >
                <ComboboxValue placeholder="Select a role" />
              </ComboboxTrigger>
              <ComboboxContent>
                <ComboboxInput placeholder="Search roles" showTrigger={false} />
                <ComboboxEmpty>No roles found.</ComboboxEmpty>
                <ComboboxList>
                  {(role) => (
                    <ComboboxItem
                      className="capitalize"
                      disabled={role === person.role}
                      key={role}
                      value={role}
                    >
                      {roleLabel(role)}
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
          </div>
          <DialogFooter>
            <Button disabled={promoteRole.isPending} onClick={close} variant="outline">
              Cancel
            </Button>
            <Button
              disabled={!pendingRole}
              loading={promoteRole.isPending}
              onClick={() => {
                if (!pendingRole) return;
                promoteRole.mutate(
                  { staffId: person.id, role: pendingRole },
                  { onError: reportError("Couldn't change the role") },
                );
                close();
              }}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rank — RIDER-031/032, TSESSION-012…015.
          Radio group rather than a combobox: four fixed values that never grow,
          and seeing all of them at once is what makes "currently Bronze"
          meaningful. */}
      <Dialog
        onOpenChange={closeOnDismiss}
        open={dialog === "rider-rank" || dialog === "therapist-rank"}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Change {rankKind === "therapist-rank" ? "therapist" : "rider"} rank
            </DialogTitle>
            <DialogDescription>
              {person.name} &middot; currently{" "}
              {rankLabel(rankKind === "therapist-rank" ? person.therapist_rank : person.rider_rank)}
              {" · "}
              {person.rank_is_manual
                ? "set by hand — the monthly system skips this rank"
                : "managed by the monthly system"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* What the monthly system last computed — the number a manual
                change is overriding. Incomplete scores never apply (0329). */}
            {latestScore.data ? (
              <p className="rounded-md border border-border px-3 py-2 text-xs text-muted-foreground">
                Last computed ({latestScore.data.cycle_month.slice(0, 7)}):{" "}
                <span className="font-medium text-foreground">
                  {rankLabel(latestScore.data.tier)} ·{" "}
                  {Number(latestScore.data.total_score).toFixed(2)}
                </span>
                {latestScore.data.is_complete ? "" : " · incomplete — won't apply"}
              </p>
            ) : null}
            <fieldset className="space-y-2">
              <legend className="mb-2 block text-sm font-medium">Rank</legend>
              <RadioGroup
                onValueChange={(value) => setPendingRank(value as RiderRank)}
                value={pendingRank ?? ""}
              >
                {RIDER_RANKS.map((rank) => (
                  <label
                    className="flex items-center gap-3 rounded-md border border-border px-3 py-2"
                    htmlFor={`rank-${person.id}-${rank}`}
                    key={rank}
                  >
                    <RadioGroupItem id={`rank-${person.id}-${rank}`} value={rank} />
                    <span className="text-sm">{rankLabel(rank)}</span>
                  </label>
                ))}
              </RadioGroup>
            </fieldset>

            <div className="space-y-2">
              <label className="block text-sm font-medium" htmlFor={`rank-reason-${person.id}`}>
                Reason (required)
              </label>
              <Textarea
                id={`rank-reason-${person.id}`}
                onChange={(event) => setRankReason(event.target.value)}
                placeholder="Why is this rank changing?"
                rows={2}
                value={rankReason}
              />
              {/* Not a courtesy note. RIDER-032 puts the previous rank, the new
                  rank, the acting admin and the reason on an audit record, and
                  the person typing should know that is what they are writing. */}
              <p className="text-xs text-muted-foreground">
                Recorded in the audit log with your name and the previous rank. Setting a rank by
                hand marks it manual — the monthly system will skip it until released.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button disabled={rankPending} onClick={close} variant="outline">
              Cancel
            </Button>
            {person.rank_is_manual ? (
              <Button
                // 0329 — the same reason box serves the release: handing a
                // rank back is an audited rank decision, not a silent reset.
                disabled={rankReason.trim().length === 0}
                loading={rankPending}
                onClick={() => {
                  const reason = rankReason.trim();
                  if (!reason) return;
                  releaseRank.mutate(
                    { staffId: person.id, reason },
                    {
                      onSuccess: () => {
                        toast.success("Rank returned to the monthly system");
                        close();
                      },
                      onError: reportError("Couldn't release the rank"),
                    },
                  );
                }}
                variant="secondary"
              >
                Release to monthly system
              </Button>
            ) : null}
            <Button
              // Both required. A rank change with no stated reason is a change
              // nobody can account for later (RIDER-032).
              disabled={!pendingRank || rankReason.trim().length === 0}
              loading={rankPending}
              onClick={() => {
                const reason = rankReason.trim();
                if (!pendingRank || !reason) return;
                const input = { staffId: person.id, rank: pendingRank, reason };
                const options = {
                  onSuccess: () => {
                    toast.success("Rank saved");
                    close();
                  },
                  onError: reportError("Couldn't change the rank"),
                };
                if (rankKind === "therapist-rank") setTherapistRank.mutate(input, options);
                else setRiderRank.mutate(input, options);
              }}
            >
              Change rank
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hub */}
      <Dialog onOpenChange={closeOnDismiss} open={dialog === "hub"}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reassign Hub</DialogTitle>
            <DialogDescription>Select a new hub for {person.name}.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <p className="mb-2 block text-sm font-medium">Hub</p>
            <HubSelect
              ariaLabel="Select a hub"
              className="w-full"
              hubs={hubs ?? []}
              allowAll={false}
              disableHubIds={person.hubId ? [person.hubId] : []}
              variant="combobox"
              value={pendingHubId}
              onValueChange={(hubId) => setPendingHubId(hubId ?? "")}
            />
          </div>
          <DialogFooter>
            <Button disabled={reassignHub.isPending} onClick={close} variant="outline">
              Cancel
            </Button>
            <Button
              disabled={!pendingHubId}
              loading={reassignHub.isPending}
              onClick={() => {
                if (!pendingHubId) return;
                reassignHub.mutate(
                  { staffId: person.id, hubId: pendingHubId },
                  { onError: reportError("Couldn't reassign the hub") },
                );
                close();
              }}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
